import { PricePerAfterKM, Setting, VehicleModel } from "models/Setting";
import React, { useEffect, useState } from "react";
import { Col, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { toast } from "react-toastify";
toast.configure();
import "react-toastify/dist/ReactToastify.css";
import { AdminService } from "services/AdminService";
import { KMTypes } from "enum/Setting";
import { Select } from "antd";
import { SettingsType } from "enum/SettingsType";
import {
  ALL_SERVICE_PREFIXES,
  SERVICE_TERRITORIES,
  ServiceTerritory,
} from "./territories";

export interface Props {
  isOpen: boolean;
  handlePopup: () => void;
  isModified: boolean;
  setIsModified: (value: boolean) => void;
  settings?: Setting[];
}

const UpdateService: React.FC<Props> = ({ isOpen, handlePopup, isModified, setIsModified, settings }: Props) => {
  const [settingValues, setSettingValues] = useState<Setting>();
  const [vehiclePrice, setVehiclePrice] = useState([] as VehicleModel[]);

  const [selectedAfterKM, setSelectedAfterKM] = useState<string>();
  const [priceForAfterKM, setPriceForAfterKM] = useState<number>();

  const [basePriceErr, setBasePriceErr] = useState<string>();
  const [additionalKMErr, setAdditionalKMErr] = useState<string>();
  const [afterKMPriceErr, setAfterKMPriceErr] = useState<string>();
  const [baseWeightErr, setBaseWeightErr] = useState<string>();
  const [vehiclePriceErr, setVehiclePriceErr] = useState<string>();

  const [settingsType, setSettingsType] = useState<SettingsType | undefined>(undefined);
  const [surgePrice, setSurgePrice] = useState<number>(0);

  const updateSetting = (
    name: keyof Setting,
    value: Setting[keyof Setting]
  ) => {
    setSettingValues(previous => ({ ...previous, [name]: value }));
  };

  const toggleTerritory = (territory: ServiceTerritory, enabled: boolean) => {
    const current = new Set(settingValues?.allowedPostalPrefixes || []);
    territory.prefixes.forEach(prefix =>
      enabled ? current.add(prefix) : current.delete(prefix)
    );
    updateSetting("allowedPostalPrefixes", Array.from(current).sort());
  };

  const hasTerritory = (territory: ServiceTerritory) =>
    territory.prefixes.every(prefix =>
      (settingValues?.allowedPostalPrefixes || []).includes(prefix)
    );

  useEffect(() => {
    handleSettingsTypeChange(settings?.length === 0 ? SettingsType.ECONOMY : settings![0].settingsType !== undefined ? settings![0].settingsType : SettingsType.ECONOMY);
  }, [settings]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const value = event.target.value;

    switch (name) {
      case "basePrice":
        if (value == "" || value == undefined || value == null) {
          setBasePriceErr("Required");
        } else {
          setBasePriceErr(undefined);
        }
        setSettingValues({ ...settingValues, basePrice: Number(value) });
        break;
      case "pricePerAdditionalKM":
        if (value == "" || value == undefined || value == null) {
          setAdditionalKMErr("Required");
        } else {
          setAdditionalKMErr(undefined);
        }
        setSettingValues({ ...settingValues, pricePerAdditionalKM: Number(value) });
        break;
      case "priceForAfterKM":
        if (value == "" || value == undefined || value == null) {
          setAfterKMPriceErr("Required");
        } else {
          setAfterKMPriceErr(undefined);
        }
        setPriceForAfterKM(Number(value));
        break;
      case "baseWeightPrice":
        if (value == "" || value == undefined || value == null) {
          setBaseWeightErr("Required");
        } else {
          setBaseWeightErr(undefined);
        }
        setSettingValues({ ...settingValues, baseWeightPrice: Number(value) });
        break;
      case "surgePrice":
        setSurgePrice(Number(value));
        break;
      default:
        break;
    }
  };

  const handleDropdown = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAfterKM(e.target.value);
  };

  const handleUpdate = () => {
    if (settingsType === undefined) {
      toast.error("Service Type is required", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
        style: { marginBottom: "4rem" },
      });
    } else if (basePriceErr !== undefined) {
      toast.error("Base Price is required", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
        style: { marginBottom: "4rem" },
      });
    } else if (additionalKMErr !== undefined) {
      toast.error("Additional KM Price is required", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
        style: { marginBottom: "4rem" },
      });
    } else if (afterKMPriceErr !== undefined) {
      toast.error("After 45km Price is required", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
        style: { marginBottom: "4rem" },
      });
    } else if (baseWeightErr !== undefined) {
      toast.error("Weight Price is required", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
        style: { marginBottom: "4rem" },
      });
    } else if (vehiclePriceErr !== undefined) {
      toast.error("Vehicle Price Price is required", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
        style: { marginBottom: "4rem" },
      });
    } else {
      const pricePerAfterKM: PricePerAfterKM = {
        km: selectedAfterKM,
        price: priceForAfterKM,
      };

      const settingsData: Setting = {
        settingsType: settingsType,
        basePrice: settingValues?.basePrice,
        pricePerAdditionalKM: settingValues?.pricePerAdditionalKM,
        pricePerAfterKM: pricePerAfterKM,
        baseWeightPrice: settingValues?.baseWeightPrice,
        pricePerWeightAfter10: 0.2,
        vehicles: vehiclePrice,
        hst: 13,
        surgePrice: surgePrice,
        flatPricingEnabled: settingValues?.flatPricingEnabled === true,
        flatPrice: Number(settingValues?.flatPrice || 0),
        includedDistanceKm: Number(settingValues?.includedDistanceKm ?? 50),
        additionalKmPrice: Number(settingValues?.additionalKmPrice || 0),
        territoryRestrictionsEnabled:
          settingValues?.territoryRestrictionsEnabled !== false,
        allowedPostalPrefixes:
          settingValues?.allowedPostalPrefixes || ALL_SERVICE_PREFIXES,
        vehicleChargesEnabled: settingValues?.vehicleChargesEnabled === true,
        vehicleSurcharges: {
          car: Number(settingValues?.vehicleSurcharges?.car || 0),
          suv: Number(settingValues?.vehicleSurcharges?.suv || 0),
          minivan: Number(settingValues?.vehicleSurcharges?.minivan || 0),
        },
        weightChargesEnabled: settingValues?.weightChargesEnabled === true,
        freeWeightUpToLb: Number(settingValues?.freeWeightUpToLb || 0),
        additionalPricePerLb: Number(settingValues?.additionalPricePerLb || 0),
        taxEnabled: settingValues?.taxEnabled !== false,
        isActive: settingValues?.isActive !== false,
      };

      AdminService.updateSettings(settingsData)
        .then(res => {
          if (res.success) {
            setIsModified(!isModified);
            toast.success("Service updated successfully.", {
              position: toast.POSITION.BOTTOM_RIGHT,
              className: "foo-bar",
              style: { marginBottom: "4rem" },
            });
            handlePopup();
          } else {
            toast.error(res.error, {
              position: toast.POSITION.BOTTOM_RIGHT,
              className: "foo-bar",
              style: { marginBottom: "4rem" },
            });
          }
        })
        .catch(error => {
          console.error(error);
        });
    }
  };

  const handleSettingsTypeChange = async (value: SettingsType) => {
    setSettingsType(value);

    if (!settings) return;

    if (settings !== undefined) {
      const selected = settings.find(s => s.settingsType === value);
      if (selected) {
        setSettingValues({
          ...selected,
          includedDistanceKm: selected.includedDistanceKm ?? 50,
          territoryRestrictionsEnabled:
            selected.territoryRestrictionsEnabled !== false,
          allowedPostalPrefixes:
            selected.allowedPostalPrefixes ?? ALL_SERVICE_PREFIXES,
          taxEnabled: selected.taxEnabled !== false,
          isActive: selected.isActive !== false,
        });
        setSelectedAfterKM(selected.pricePerAfterKM?.km);
        setPriceForAfterKM(selected.pricePerAfterKM?.price);
        setVehiclePrice(selected.vehiclePrice ?? []);
        setSurgePrice(selected.surgePrice ?? 0);
      } else {
        const vehicles: VehicleModel[] = [
          { vehicle: "car", price: 0, brand: "", model: "", year: "" },
          { vehicle: "suv", price: 0, brand: "", model: "", year: "" },
          { vehicle: "van", price: 0, brand: "", model: "", year: "" },
        ];

        setSelectedAfterKM('');
        setPriceForAfterKM(0);
        setSettingValues({
          basePrice: 0,
          pricePerAdditionalKM: 0,
          baseWeightPrice: 0,
          flatPricingEnabled: false,
          flatPrice: 0,
          includedDistanceKm: 50,
          additionalKmPrice: 0,
          territoryRestrictionsEnabled: true,
          allowedPostalPrefixes: ALL_SERVICE_PREFIXES,
          vehicleChargesEnabled: false,
          vehicleSurcharges: { car: 0, suv: 0, minivan: 0 },
          weightChargesEnabled: false,
          freeWeightUpToLb: 0,
          additionalPricePerLb: 0,
          taxEnabled: true,
          isActive: true,
        });
        setVehiclePrice(vehicles);
        setSurgePrice(0);
      }
    }
  };

  const handleClose = () => {
    setBasePriceErr(undefined);
    setAdditionalKMErr(undefined);
    setAfterKMPriceErr(undefined);
    setBaseWeightErr(undefined);
    setVehiclePriceErr(undefined);
    setIsModified(!isModified);
    handlePopup();
  };
  const closeBtn = <button onClick={handleClose} type="button" className="btn-close" />;

  return (
    <React.Fragment>
      <Modal isOpen={isOpen} toggle={handlePopup} size="lg" backdrop="static">
        <ModalHeader toggle={handlePopup} close={closeBtn}>
          Update Service
        </ModalHeader>
        <ModalBody>
          <div className="content clearfix">
            <Row>
              <Col lg={6} className="mb-3">
                <Label>Service Type</Label>
                <Select value={settingsType} onChange={handleSettingsTypeChange} className="form-select">
                  <Select.Option value={SettingsType.ECONOMY}>Economy 8hrs</Select.Option>
                  <Select.Option value={SettingsType.STANDARD}>Standard 8-6</Select.Option>
                  <Select.Option value={SettingsType.PRIORITY}>Priority 6-4</Select.Option>
                  <Select.Option value={SettingsType.RUSH}>Rush 4-2</Select.Option>
                  <Select.Option value={SettingsType.AFTER_HOURS}>After Hours (3PM–9PM)</Select.Option>
                </Select>
              </Col>
              <Col lg={3} className="mb-3">
                <Label>Flat Pricing</Label>
                <select className="form-select" value={settingValues?.flatPricingEnabled ? "yes" : "no"} onChange={e => updateSetting("flatPricingEnabled", e.target.value === "yes")}>
                  <option value="yes">Yes</option><option value="no">No</option>
                </select>
              </Col>
              <Col lg={3} className="mb-3">
                <Label>Status</Label>
                <select className="form-select" value={settingValues?.isActive === false ? "inactive" : "active"} onChange={e => updateSetting("isActive", e.target.value === "active")}>
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select>
              </Col>
            </Row>

            {settingValues?.flatPricingEnabled ? (
              <Row>
                <Col lg={4} className="mb-3">
                  <Label>Flat Price</Label>
                  <input className="form-control" type="number" min="0" step="0.01" value={settingValues.flatPrice ?? 0} onChange={e => updateSetting("flatPrice", Number(e.target.value))} />
                </Col>
                <Col lg={4} className="mb-3">
                  <Label>Included Distance (KM)</Label>
                  <input className="form-control" type="number" min="0" value={settingValues.includedDistanceKm ?? 50} onChange={e => updateSetting("includedDistanceKm", Number(e.target.value))} />
                </Col>
                <Col lg={4} className="mb-3">
                  <Label>Additional KM Price</Label>
                  <input className="form-control" type="number" min="0" step="0.01" value={settingValues.additionalKmPrice ?? 0} onChange={e => updateSetting("additionalKmPrice", Number(e.target.value))} />
                </Col>
              </Row>
            ) : (
              <>
                <h6 className="mt-2">Advanced Pricing</h6>
                <Row>
                  <Col lg={4} className="mb-3">
                    <Label>Base Price (up to 5 KM)</Label>
                    <input className="form-control" type="number" min="0" name="basePrice" value={settingValues?.basePrice ?? 0} onChange={handleChange} />
                  </Col>
                  <Col lg={4} className="mb-3">
                    <Label>Additional KM Price</Label>
                    <input className="form-control" type="number" min="0" name="pricePerAdditionalKM" value={settingValues?.pricePerAdditionalKM ?? 0} onChange={handleChange} />
                  </Col>
                  <Col lg={4} className="mb-3">
                    <Label>Surge Price</Label>
                    <input className="form-control" type="number" min="0" name="surgePrice" value={surgePrice} onChange={handleChange} />
                  </Col>
                </Row>
                <Row>
                  <Col lg={4} className="mb-3">
                    <Label>Higher Rate Starts After</Label>
                    <select className="form-select" value={selectedAfterKM} onChange={handleDropdown}>
                      <option value={KMTypes.KMAfter30}>30 KM</option>
                      <option value={KMTypes.KMAfter35}>35 KM</option>
                      <option value={KMTypes.KMAfter40}>40 KM</option>
                      <option value={KMTypes.KMAfter45}>45 KM</option>
                    </select>
                  </Col>
                  <Col lg={4} className="mb-3">
                    <Label>Price Per KM After Threshold</Label>
                    <input className="form-control" type="number" min="0" step="0.01" name="priceForAfterKM" value={priceForAfterKM ?? 0} onChange={handleChange} />
                  </Col>
                  <Col lg={4} className="mb-3">
                    <Label>Legacy Price Per lb</Label>
                    <input className="form-control" type="number" min="0" step="0.01" name="baseWeightPrice" value={settingValues?.baseWeightPrice ?? 0} onChange={handleChange} />
                  </Col>
                </Row>
              </>
            )}

            <hr />
            <Row>
              <Col lg={4} className="mb-3">
                <Label>Apply Territory Restrictions</Label>
                <select className="form-select" value={settingValues?.territoryRestrictionsEnabled === false ? "no" : "yes"} onChange={e => updateSetting("territoryRestrictionsEnabled", e.target.value === "yes")}>
                  <option value="yes">Yes</option><option value="no">No</option>
                </select>
              </Col>
            </Row>
            {settingValues?.territoryRestrictionsEnabled !== false && (
              <div className="border rounded p-3 mb-3" style={{ maxHeight: 280, overflowY: "auto" }}>
                <div className="d-flex justify-content-between mb-2">
                  <strong>Allowed territories (first 3 postal-code characters)</strong>
                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => updateSetting("allowedPostalPrefixes", ALL_SERVICE_PREFIXES)}>Select all</button>
                </div>
                {SERVICE_TERRITORIES.map(territory => (
                  <div className="form-check mb-2" key={territory.name}>
                    <input className="form-check-input" type="checkbox" checked={hasTerritory(territory)} onChange={e => toggleTerritory(territory, e.target.checked)} id={`territory-${territory.name}`} />
                    <label className="form-check-label" htmlFor={`territory-${territory.name}`}>
                      <strong>{territory.name}</strong><br /><small>{territory.prefixes.join(", ")}</small>
                    </label>
                  </div>
                ))}
                <Label className="mt-2">Allowed FSA prefixes</Label>
                <textarea
                  key={(settingValues?.allowedPostalPrefixes || []).join(",")}
                  className="form-control"
                  rows={3}
                  defaultValue={(settingValues?.allowedPostalPrefixes || []).join(", ")}
                  onBlur={e =>
                    updateSetting(
                      "allowedPostalPrefixes",
                      Array.from(
                        new Set(
                          e.target.value
                            .toUpperCase()
                            .split(/[\s,]+/)
                            .filter(prefix => /^[A-Z]\d[A-Z]$/.test(prefix))
                        )
                      )
                    )
                  }
                />
              </div>
            )}

            <Row>
              <Col lg={4} className="mb-3">
                <Label>Apply Vehicle Charges</Label>
                <select className="form-select" value={settingValues?.vehicleChargesEnabled ? "yes" : "no"} onChange={e => updateSetting("vehicleChargesEnabled", e.target.value === "yes")}>
                  <option value="yes">Yes</option><option value="no">No</option>
                </select>
              </Col>
              <Col lg={4} className="mb-3">
                <Label>Apply Weight Charges</Label>
                <select className="form-select" value={settingValues?.weightChargesEnabled ? "yes" : "no"} onChange={e => updateSetting("weightChargesEnabled", e.target.value === "yes")}>
                  <option value="yes">Yes</option><option value="no">No</option>
                </select>
              </Col>
              <Col lg={4} className="mb-3">
                <Label>Apply Tax (HST 13%)</Label>
                <select className="form-select" value={settingValues?.taxEnabled === false ? "no" : "yes"} onChange={e => updateSetting("taxEnabled", e.target.value === "yes")}>
                  <option value="yes">Yes</option><option value="no">No</option>
                </select>
              </Col>
            </Row>
            {settingValues?.vehicleChargesEnabled && (
              <Row>
                {(["car", "suv", "minivan"] as const).map((vehicle, index) => (
                  <Col lg={4} className="mb-3" key={vehicle}>
                    <Label>{index === 0 ? "Car (0–149 lb)" : index === 1 ? "SUV (150–349 lb)" : "Minivan (350–499 lb)"} Surcharge</Label>
                    <input className="form-control" type="number" min="0" step="0.01" value={settingValues?.vehicleSurcharges?.[vehicle] ?? 0} onChange={e => updateSetting("vehicleSurcharges", { ...settingValues?.vehicleSurcharges, [vehicle]: Number(e.target.value) })} />
                  </Col>
                ))}
              </Row>
            )}
            {settingValues?.weightChargesEnabled && (
              <Row>
                <Col lg={6} className="mb-3">
                  <Label>Free Weight Up To (lb)</Label>
                  <input className="form-control" type="number" min="0" value={settingValues.freeWeightUpToLb ?? 0} onChange={e => updateSetting("freeWeightUpToLb", Number(e.target.value))} />
                </Col>
                <Col lg={6} className="mb-3">
                  <Label>Additional Price Per lb</Label>
                  <input className="form-control" type="number" min="0" step="0.01" value={settingValues.additionalPricePerLb ?? 0} onChange={e => updateSetting("additionalPricePerLb", Number(e.target.value))} />
                </Col>
              </Row>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-primary btn-sm px-5 py-2 mt-2 rounded-1 w-100" onClick={handleUpdate}>
            Update Service
          </button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default UpdateService;
